import React, { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import Chart from "react-apexcharts";
import {
  FaArrowRight,
  FaBoxesStacked,
  FaClock,
  FaDatabase,
  FaFolderOpen,
  FaHardDrive,
  FaServer,
  FaWandMagicSparkles,
} from "react-icons/fa6";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import RecentActivityCard from "./RecentActivityCard";
import "./Dashboard.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

function formatDateTime(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("pt-BR");
}

export default function Dashboard() {
  const { userInfo, loading } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState("");
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [showTrialAlert, setShowTrialAlert] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const API_BASE = import.meta.env.VITE_BACKEND_URL;
  const navigate = useNavigate();

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 720px)");
    const onChange = (event) => setIsMobile(event.matches);
    setIsMobile(mql.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (loading) return;

    async function fetchMetrics() {
      const token = localStorage.getItem("token");
      if (!token) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/bucket-metrics`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status === 401) {
          localStorage.removeItem("token");
          navigate("/login");
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setMetrics(data);
      } catch (err) {
        console.error("Erro ao buscar métricas:", err);
        setError("Erro ao carregar métricas.");
      }
    }

    fetchMetrics();
  }, [API_BASE, loading, navigate]);

  useEffect(() => {
    if (!loading && userInfo?.plan === "starter" && userInfo?.createdAt) {
      const created = new Date(userInfo.createdAt);
      const now = new Date();
      const diffDays = Math.floor((now - created) / (1000 * 60 * 60 * 24));
      const remaining = 30 - diffDays;
      setDaysRemaining(remaining > 0 ? remaining : 0);
    }
  }, [loading, userInfo]);

  const bucketCount = metrics?.totalBuckets || 0;
  const totalObjects = metrics?.totalObjects || 0;
  const bucketList = metrics?.buckets || [];

  const largestBucket = useMemo(() => {
    if (!bucketList.length) return null;
    return [...bucketList].sort((a, b) => (b.totalSize || 0) - (a.totalSize || 0))[0];
  }, [bucketList]);

  const busiestBucket = useMemo(() => {
    if (!bucketList.length) return null;
    return [...bucketList].sort((a, b) => (b.objectsCount || 0) - (a.objectsCount || 0))[0];
  }, [bucketList]);

  const latestBucketUpdate = useMemo(() => {
    if (!bucketList.length) return null;
    return [...bucketList]
      .filter((bucket) => bucket.ultimaModificacao)
      .sort(
        (a, b) =>
          new Date(b.ultimaModificacao).getTime() -
          new Date(a.ultimaModificacao).getTime()
      )[0] || null;
  }, [bucketList]);

  const averageObjects = bucketCount
    ? Math.round(totalObjects / bucketCount).toLocaleString("pt-BR")
    : "0";

  const apexOptions = useMemo(
    () => ({
      chart: {
        id: "bucket-comparison",
        stacked: false,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: ["#2457d6", "#ff8a3d"],
      stroke: { curve: "smooth", width: 3 },
      fill: {
        type: "gradient",
        gradient: { opacityFrom: 0.3, opacityTo: 0.05 },
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: "rgba(25, 40, 75, 0.08)",
        strokeDashArray: 4,
      },
      xaxis: {
        categories: bucketList.map((bucket) => bucket.name),
        labels: { rotate: -20, style: { colors: "#60708f" } },
      },
      yaxis: [
        {
          title: { text: "Objetos" },
          labels: { formatter: (val) => Math.round(val).toLocaleString("pt-BR") },
        },
        {
          opposite: true,
          title: { text: "Tamanho (GiB)" },
          labels: { formatter: (val) => `${val.toFixed(1)} GiB` },
        },
      ],
      legend: { position: "top", horizontalAlign: "left" },
      tooltip: { shared: true },
    }),
    [bucketList]
  );

  const apexSeries = useMemo(
    () => [
      {
        name: "Objetos",
        data: bucketList.map((bucket) => bucket.objectsCount || 0),
      },
      {
        name: "Tamanho (GiB)",
        data: bucketList.map((bucket) => {
          const gib = (bucket.totalSize || 0) / 1024 ** 3;
          return Number.isFinite(gib) ? Number(gib.toFixed(1)) : 0;
        }),
      },
    ],
    [bucketList]
  );

  const bucketChartData = useMemo(
    () => ({
      labels: bucketList.map((bucket) => bucket.name),
      datasets: [
        {
          label: "Uso de Armazenamento (GiB)",
          data: bucketList.map((bucket) => {
            const gib = (bucket.totalSize || 0) / 1024 ** 3;
            return Number.isFinite(gib) ? Number(gib.toFixed(1)) : 0;
          }),
          backgroundColor: [
            "#1f4fd1",
            "#3f7cff",
            "#20a39e",
            "#ff8a3d",
            "#ffbc42",
            "#6c63ff",
          ],
          borderRadius: 10,
          borderSkipped: false,
        },
      ],
    }),
    [bucketList]
  );

  const bucketChartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { display: false }, ticks: { color: "#60708f" } },
        y: {
          beginAtZero: true,
          ticks: { color: "#60708f" },
          grid: { color: "rgba(25, 40, 75, 0.08)" },
        },
      },
    }),
    []
  );

  if (loading) {
    return <p className="loading-message">Carregando informações do usuário...</p>;
  }

  if (!metrics && !error) {
    return <p className="loading-message">Carregando dados do painel...</p>;
  }

  if (error) {
    return <p className="error-message">Erro: {error}</p>;
  }

  return (
    <div className="dashboard-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero-copy">
          <span className="dashboard-chip">
            <FaWandMagicSparkles />
            Console ViaDrive
          </span>
          <h1>
            Visão geral da operação de <strong>{userInfo?.firstName || "sua equipe"}</strong>
          </h1>
          <p>
            Acompanhe buckets, uso de armazenamento e atividade recente em um painel
            mais claro para decisões rápidas.
          </p>
          <div className="dashboard-hero-actions">
            <button
              type="button"
              className="dashboard-primary-btn"
              onClick={() => navigate("/buckets")}
            >
              Ver buckets
              <FaArrowRight />
            </button>
            <button
              type="button"
              className="dashboard-secondary-btn"
              onClick={() => navigate("/users")}
            >
              Gerenciar usuários
            </button>
          </div>
        </div>

        <div className="dashboard-hero-panel">
          <div className="hero-stat">
            <span>Plano atual</span>
            <strong>{userInfo?.plan || "starter"}</strong>
          </div>
          <div className="hero-stat">
            <span>Bucket com maior volume</span>
            <strong>{largestBucket?.name || "N/A"}</strong>
            <small>{largestBucket?.tamanhoFormatado || "Sem dados"}</small>
          </div>
          <div className="hero-stat">
            <span>Última atualização</span>
            <strong>{latestBucketUpdate?.name || "N/A"}</strong>
            <small>{formatDateTime(latestBucketUpdate?.ultimaModificacao)}</small>
          </div>
        </div>
      </section>

      {userInfo?.plan === "starter" && daysRemaining !== null && showTrialAlert && (
        <div
          className={`dashboard-trial-alert ${
            daysRemaining <= 5 ? "is-critical" : ""
          }`}
        >
          <div>
            <strong>Período de testes:</strong> restam {daysRemaining} dias para
            finalizar a ativação da conta.
            <a href="/upgrade"> Ir para upgrade</a>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setShowTrialAlert(false)}
          />
        </div>
      )}

      <section className="dashboard-kpis">
        <article className="kpi-card accent-blue">
          <div className="kpi-icon">
            <FaFolderOpen />
          </div>
          <div>
            <span className="kpi-label">Buckets</span>
            <strong className="kpi-value">{bucketCount}</strong>
          </div>
        </article>

        <article className="kpi-card accent-green">
          <div className="kpi-icon">
            <FaBoxesStacked />
          </div>
          <div>
            <span className="kpi-label">Objetos</span>
            <strong className="kpi-value">{totalObjects.toLocaleString("pt-BR")}</strong>
          </div>
        </article>

        <article className="kpi-card accent-orange">
          <div className="kpi-icon">
            <FaHardDrive />
          </div>
          <div>
            <span className="kpi-label">Armazenamento</span>
            <strong className="kpi-value">{metrics?.totalSizeFormatado || "0 B"}</strong>
          </div>
        </article>

        <article className="kpi-card accent-slate">
          <div className="kpi-icon">
            <FaDatabase />
          </div>
          <div>
            <span className="kpi-label">Média por bucket</span>
            <strong className="kpi-value">{averageObjects}</strong>
          </div>
        </article>
      </section>

      <section className="dashboard-insights">
        <article className="insight-card">
          <div className="insight-head">
            <span>Bucket mais ativo</span>
            <FaServer />
          </div>
          <strong>{busiestBucket?.name || "N/A"}</strong>
          <p>{(busiestBucket?.objectsCount || 0).toLocaleString("pt-BR")} objetos</p>
        </article>

        <article className="insight-card">
          <div className="insight-head">
            <span>Maior volume</span>
            <FaHardDrive />
          </div>
          <strong>{largestBucket?.name || "N/A"}</strong>
          <p>{largestBucket?.tamanhoFormatado || "Sem dados"}</p>
        </article>

        <article className="insight-card">
          <div className="insight-head">
            <span>Último movimento</span>
            <FaClock />
          </div>
          <strong>{latestBucketUpdate?.name || "N/A"}</strong>
          <p>{formatDateTime(latestBucketUpdate?.ultimaModificacao)}</p>
        </article>
      </section>

      <section className="dashboard-visuals">
        <div className="dashboard-panel chart-panel">
          <div className="panel-header">
            <div>
              <h5>Uso de armazenamento por bucket</h5>
              <p>Distribuição do volume total em GiB.</p>
            </div>
          </div>
          <div className="chart-shell" style={{ height: isMobile ? 280 : 320 }}>
            <Bar data={bucketChartData} options={bucketChartOptions} />
          </div>
        </div>

        <div className="dashboard-panel chart-panel">
          <div className="panel-header">
            <div>
              <h5>Objetos vs. tamanho</h5>
              <p>Comparativo entre densidade e consumo por bucket.</p>
            </div>
          </div>
          <div className="chart-shell" style={{ height: isMobile ? 280 : 320 }}>
            <Chart
              options={apexOptions}
              series={apexSeries}
              type="area"
              height={isMobile ? 280 : 320}
            />
          </div>
        </div>
      </section>

      <section className="dashboard-buckets">
        <div className="panel-header">
          <div>
            <h5>Detalhes dos buckets</h5>
            <p>Navegue rapidamente para cada área de armazenamento.</p>
          </div>
        </div>

        <div className="bucket-grid">
          {bucketList.map((bucket) => (
            <article
              key={bucket.name}
              className="bucket-modern-card"
              onClick={() => navigate(`/buckets/${bucket.name}/objects`)}
            >
              <div className="bucket-modern-top">
                <div className="bucket-badge">
                  <FaFolderOpen />
                </div>
                <span>{bucket.tamanhoFormatado}</span>
              </div>

              <h6>{bucket.name}</h6>

              <div className="bucket-modern-meta">
                <div>
                  <span>Objetos</span>
                  <strong>{(bucket.objectsCount || 0).toLocaleString("pt-BR")}</strong>
                </div>
                <div>
                  <span>Criado em</span>
                  <strong>{formatDateTime(bucket.criadoEm)}</strong>
                </div>
                <div>
                  <span>Atualizado em</span>
                  <strong>{formatDateTime(bucket.ultimaModificacao)}</strong>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="dashboard-activity">
        <RecentActivityCard />
      </section>
    </div>
  );
}
